import { Property, PropertyContact } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { z } from 'zod';

import { PropertyClientService, PropertyContactClientService } from '../tools/helpers';

export const propertyFormSchema = z.object({
  streetAddress: z.string().min(1, { message: 'Street address is required' }),
  city: z.string().min(1, { message: 'City is required' }),
  state: z.string().min(1, { message: 'State is required' }),
  zipCode: z
    .string()
    .min(1, { message: 'ZIP code is required' })
    .regex(/^\d{5}(-\d{4})?$/, {
      message: 'ZIP code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)',
    }),
  storeNumber: z.string().optional(),
  propertyOwnershipTypeId: z.string().min(1, { message: 'Property ownership is required' }),
  directions: z.string().optional(),
  subdivision: z.string().optional(),
  isResidential: z.string().optional(),
  notes: z.string().optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  contactTitle: z.string().optional(),
});
export type PropertyFormData = z.infer<typeof propertyFormSchema>;

export const propertyToFormValues = (property?: Property): PropertyFormData => ({
  streetAddress: property?.address ?? '',
  city: property?.city ?? '',
  state: property?.state ?? '',
  zipCode: property?.zip ?? '',
  storeNumber: property?.additionalAddressLine ?? '',
  propertyOwnershipTypeId: String(property?.propertyOwnershipTypeId || 1),
  directions: property?.directions ?? '',
  subdivision: property?.subdivision ?? '',
  isResidential: property ? String(property.isResidential) : '1',
  notes: property?.notes ?? '',
  contactName: '',
  contactPhone: '',
  contactTitle: '',
});

export const applyFormValues = (data: PropertyFormData, base?: Property): Property => {
  const property = base ? Property.clone(base) : Property.create();
  property.address = data.streetAddress;
  property.city = data.city;
  property.state = data.state;
  property.zip = data.zipCode;
  property.additionalAddressLine = data.storeNumber?.trim() ?? '';
  property.propertyOwnershipTypeId = Number(data.propertyOwnershipTypeId || 1);
  property.directions = data.directions?.trim() ?? '';
  property.subdivision = data.subdivision?.trim() ?? '';
  property.isResidential = data.isResidential ? Number(data.isResidential) : 0;
  property.notes = data.notes ?? '';
  return property;
};

export const createPropertyWithContact = async (
  data: PropertyFormData,
  userId: number,
  geo?: { geolocationLat: number; geolocationLng: number },
): Promise<Property | undefined> => {
  const prop = applyFormValues(data);
  prop.userId = userId;
  prop.isActive = 1;
  if (geo) {
    prop.geolocationLat = geo.geolocationLat;
    prop.geolocationLng = geo.geolocationLng;
  }
  const created = await PropertyClientService.Create(prop);

  const contactName = data.contactName?.trim() ?? '';
  const contactPhone = data.contactPhone?.trim() ?? '';
  if (created && (contactName || contactPhone)) {
    const [firstName, ...rest] = contactName.split(' ');
    try {
      await PropertyContactClientService.Create(
        PropertyContact.create({
          propertyId: created.id,
          firstName: firstName ?? '',
          lastName: rest.join(' '),
          phone: contactPhone,
          title: data.contactTitle?.trim() ?? '',
          showOnJobs: 1,
          isActive: 1,
        }),
      );
    } catch {
      toast({
        title: 'Property created, but the point of contact could not be saved',
        variant: 'destructive',
      });
    }
  }
  return created;
};
